import { FC, Fragment, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  CommonStyle,
  ControlledForm,
  ControlledTable,
  Filter2
} from "Components";
import { RouteUrls } from "Constant/RouteUrls";
import CommonFormatter from "Utils/Common.Formatter";
import { TableLayout } from "Layouts";
import {
  actionArr,
  FilterArr,
  FormDataObj,
  TableCol,
} from "./Data";
import { useNavigate } from "react-router-dom";
import CstmBtn from "Components/CstmBtn";
import { DropdownArrPropsType, SingleSelectValType } from "DataTypes/Form";
import ProblemApi from "Service/Problem.api";
import ProbFormatter from "Utils/Prob.Formatter";
import { FormatDropdownData } from "Utils/DataFormatter";
import PreApprovedPlanApi from "Service/PreApprovedPlan.api";
import PreApprovedPlanFormatter from "Utils/PreApprovedPlan.Formatter";
import { CheckNumber } from "Utils/common";
import { useDispatch, useSelector } from "react-redux";
import {
  setAlertData,
  setPageFilter,
  setPageLimit,
  startLoading,
  stopLoading
} from "Redux/Screen/Action";
import {
  ScreenInitialStateType,
  UserInitialStateType
} from "DataTypes/Redux";
import { PreApprovedPlanListType } from "DataTypes/PatientManagement.type";
import { ModalLayout } from "Layouts";
import { CommonButtonArray, DeleteButtonArray } from "Components/Modal/Data";
import { Filter2DataProps } from "DataTypes/Common";
import {
  CreateFilter2Payload,
  RedoxDataToFilter,
  TransformFilterDataRedox
} from "Utils/common";
import UI from "Constant/UI";

const MyPreApprovedPlans: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const { listPagination, listFilters }: ScreenInitialStateType = useSelector(
    (state: any) => state?.screen
  );
  const listObj = listFilters.find(el => el.id == "my-pre-approved-plan");
  const reduxLimit = (listPagination || []).find(
    el => el.id == "my-pre-approved-plan"
  )?.limit || 10;
  const [rows, setRows] = useState<PreApprovedPlanListType[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [filterData, setFilterData] = useState<Filter2DataProps[]>(() =>
    RedoxDataToFilter(
      JSON.parse(JSON.stringify(FilterArr())),
      listObj
    )
  );
  const [pageData, setPageData] = useState({
    ...UI.pageData,
    limit: reduxLimit
  });
  const [searchText, setSearchText] = useState("");
  const [problemSearch, setProblemSearch] = useState("");
  const [problemDropArr, setProblemDropArr] =
    useState<DropdownArrPropsType[]>([]);
  const [problemDropVal, setProblemDropVal] =
    useState<SingleSelectValType>(null);
  const [deletePlan, setDeletePlan] =
    useState<PreApprovedPlanListType | null>(null);
  const [disablePlan, setDisablePlan] =
    useState<PreApprovedPlanListType | null>(null);
  const [enablePlan, setEnablePlan] =
    useState<PreApprovedPlanListType | null>(null);
  const [draftPlanPrompt, setDraftPlanPrompt] = useState<any>(null);
  const [editDraftSource, setEditDraftSource] =
    useState<PreApprovedPlanListType | null>(null);
  const tableHeight = `calc(100% - ${filterHeight}px)`;

  useEffect(() => {
    CommonFormatter.HandleNavArr({
      id: "myPreApprovedPlans",
      label: "My Pre-Approved Plan(s)",
      path: `/${RouteUrls.hs}/${RouteUrls.myPreApprovedPlans}`,
      level: 1,
      link: RouteUrls.myPreApprovedPlans,
      navType: "my-items",
    });
    handleProblemSearch();
  }, []);

  useEffect(() => {
    handleSetData(1, pageData.limit);
  }, [filterData, myInfo.id]);

  const handleProblemSearch: (
    searchVal?: string
  ) => void = async (searchVal) => {
    const newSearchVal = searchVal || "";
    const res = await ProblemApi.list({
      limit: 20,
      offset: 0,
      data: { problemName: newSearchVal },
    });
    let newProbDrop: DropdownArrPropsType[] = [];
    if (res?.success) {
      const formattedData = ProbFormatter.List(res?.data);
      newProbDrop = FormatDropdownData(formattedData, "id", "name", "id");
    }
    setProblemDropArr(newProbDrop);
  };

  const handleSetData: (
    currentPage: number,
    currentLimit: number
  ) => void = async (currentPage, currentLimit) => {
    setLoading(true);
    const newFilterPayload = CreateFilter2Payload(filterData);
    const payload = {
      ...(newFilterPayload || {}),
      createdBy: myInfo.id,
      practiceId: null,
    };
    const res = await PreApprovedPlanApi.PlanList({
      offset: (currentPage - 1) * currentLimit,
      limit: currentLimit,
      data: payload
    });
    let newPageData = { ...UI.pageData };
    let newRows: PreApprovedPlanListType[] = [];
    if (res?.success) {
      newRows = PreApprovedPlanFormatter.PlanList(res?.data);
      newPageData = res.criterion;
    }
    setRows(newRows);
    setPageData(newPageData);
    dispatch(setPageLimit({
      id: "my-pre-approved-plan",
      limit: newPageData.limit
    }));
    dispatch(setPageFilter({
      id: "my-pre-approved-plan",
      filterData: TransformFilterDataRedox(filterData)
    }));
    setLoading(false);
  };

  const handleEdit: (
    data: PreApprovedPlanListType,
    overrideDraft?: boolean
  ) => void = async (data, overrideDraft) => {
    if (data.status?.toUpperCase() == "DRAFT") {
      navigate(`./${data.id}/${data.planVersionId}/${RouteUrls.plan}`, {
        state: { planVersionId: data.planVersionId }
      });
      return;
    }
    dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
    const res = await PreApprovedPlanApi.GenerateTemplateVersion({
      data: {
        careplanId: data.id,
        planVersionId: data.planVersionId,
        createdBy: myInfo.id,
        overrideDraft
      }
    });
    if (res?.success) {
      if (res?.data?.draftExists && res?.data?.requiresConfirmation) {
        setEditDraftSource(data);
        setDraftPlanPrompt(res.data);
        dispatch(stopLoading());
        return;
      }
      navigate(
        `./${res?.data?.planId}/${res?.data?.planVersionId}` +
        `/${RouteUrls.plan}`,
        {
          state: { planVersionId: res?.data?.planVersionId }
        }
      );
    }
    dispatch(stopLoading());
  };

  const handleView: (
    data: PreApprovedPlanListType
  ) => void = (data) => {
    navigate(`./${data.id}/${data.planVersionId}/${RouteUrls.plan}`, {
      state: { planVersionId: data.planVersionId }
    });
  };

  const handleDelete: (
    data: PreApprovedPlanListType
  ) => void = (data) => {
    setDeletePlan(data);
  };

  const handleDeleteConfirm: () => void = async () => {
    if (!deletePlan?.id || !deletePlan?.planVersionId) return;
    const res = await PreApprovedPlanApi.DeletePlan({
      data: {
        careplanId: deletePlan.id,
        planVersionId: deletePlan.planVersionId
      }
    });
    if (res?.success) {
      handleSetData(pageData.page, pageData.limit);
    }
    setDeletePlan(null);
  };

  const isPlanVersionEnabled: (
    data: PreApprovedPlanListType
  ) => boolean = (data) => data.status?.toUpperCase() == "APPROVED";

  const handleEnableDisable: (
    data: PreApprovedPlanListType
  ) => void = (data) => {
    if (isPlanVersionEnabled(data)) {
      setDisablePlan(data);
      return;
    }
    setEnablePlan(data);
  };

  const handleDisableConfirm: () => void = async () => {
    if (!disablePlan) return;
    await confirmEnableDisable(disablePlan, true);
    setDisablePlan(null);
  };

  const confirmEnableDisable: (
    data: PreApprovedPlanListType | null,
    showDisableMessage?: boolean
  ) => void = async (data, showDisableMessage = false) => {
    if (!data) return;
    const res = await PreApprovedPlanApi.TogglePlanStatus({
      data: {
        planId: data.id,
        planVersionId: data.planVersionId
      }
    });
    if (res?.success) {
      dispatch(
        setAlertData(
          showDisableMessage
            ? {
              alertMessage:
                `Preapproved plan for ${data.problemName || "this problem"} ` +
                "has been disabled.",
              openAlert: true,
              alertVariant: "success",
            }
            : res?.alert
        )
      );
      handleSetData(pageData.page, pageData.limit);
    }
    setEnablePlan(null);
  };

  const handleCreatePlan: (overrideDraft?: boolean) => void = async (
    overrideDraft
  ) => {
    dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
    const res = await PreApprovedPlanApi.TemplateSearch(
      { id1: problemDropVal }
    );
    if (res.success) {
      const formattedData = PreApprovedPlanFormatter.TemplateSearch(res?.data);
      const payload = {
        practiceId: myInfo.practiceId,
        category: formattedData,
        overrideDraft
      };
      const res2 = await PreApprovedPlanApi.CreatePlan(
        { id1: problemDropVal, data: payload }
      );
      if (res2.success) {
        if (res2?.data?.draftExists && res2?.data?.requiresConfirmation) {
          setDraftPlanPrompt(res2.data);
          dispatch(stopLoading());
          return;
        }
        const planId = CheckNumber(res2?.data?.id);
        navigate(`./${planId}/${res2?.data?.planVersionId}/${RouteUrls.plan}`, {
          state: { planVersionId: res2?.data?.planVersionId }
        });
      }
    }
    dispatch(stopLoading());
  };

  const SampleUIWithFilter: React.ReactElement = (
    <Box display="flex" alignItems="end" width="100%">
      <Box flex={1} mr={1}>
        <ControlledForm.SingleApi
          {...FormDataObj}
          onClick={async (val) => {
            setProblemSearch(
              problemDropArr.find(
                findEl => findEl.value == val
              )?.label || ""
            );
            setProblemDropVal(val);
          }}
          onChangeApiSearch={true}
          inputChange={async (val) => {
            setProblemSearch(val);
            setProblemDropVal(null);
            handleProblemSearch(val);
          }}
          onIconClick={async (iconId) => {
            if (iconId == "clear") {
              setProblemSearch("");
              setProblemDropVal(null);
              handleProblemSearch("");
            }
          }}
          inputVal={problemSearch}
          dropArr={problemDropArr}
          dropVal={problemDropVal}
          apiSearch={true}
          iconSearch={true}
        />
      </Box>
      <CstmBtn
        label="Create Pre-Approved Plan"
        onClick={() => handleCreatePlan()}
        disable={!problemDropVal}
      />
    </Box>
  );

  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => TableCol()}
      tableRow={rows}
      tableAction={(data: PreApprovedPlanListType) =>
        actionArr(
          () => handleView(data),
          () => handleEdit(data),
          () => handleDelete(data),
          () => handleEnableDisable(data),
          data
        )
      }
      searchText={searchText}
    />
  );

  return (
    <Box
      height="91%"
      sx={{
        ...CommonStyle.sxWhiteBox,
        overflow: "auto",
        px: 2
      }}>
      <ModalLayout
        modwidth="30%"
        heading={"Warning"}
        content={
          <Typography textAlign="center">
            Are you sure you want to delete this draft ?
          </Typography>
        }
        onClose={() => setDeletePlan(null)}
        open={deletePlan ? true : false}
        buttonArr={DeleteButtonArray(handleDeleteConfirm, () => {
          setDeletePlan(null);
        })}
      />
      <ModalLayout
        modwidth="33%"
        heading={"Warning"}
        content={
          <Fragment>
            <Typography textAlign="center" fontWeight={600}>
              Are you sure you want to disable this Pre-Approved Plan?
            </Typography>
            <Typography mt={2}>
              <b>Existing Plans:</b>{" "}
              This pre-approved plan will be available for care plans that have
              already been started but are not signed off. Users can choose to
              edit or continue without editing the plan.
            </Typography>
            <Typography mt={1}>
              <b>New Plans:</b>{" "}
              This pre-approved plan will immediately become unavailable for any
              new care plans.
            </Typography>
            <Typography textAlign="center" mt={2}>
              Do you wish to proceed?
            </Typography>
          </Fragment>
        }
        open={disablePlan ? true : false}
        onClose={() => setDisablePlan(null)}
        buttonArr={CommonButtonArray(
          handleDisableConfirm,
          () => setDisablePlan(null),
          "Yes",
          "No"
        )}
      />
      <ModalLayout
        modwidth="34%"
        heading={"Enable Plan Version?"}
        content={
          <Fragment>
            <Typography textAlign="center">
              Are you sure you want to enable this plan version?
            </Typography>
            <Typography textAlign="center" mt={2}>
              Doing this will disable the existing enabled approved version.
            </Typography>
          </Fragment>
        }
        onClose={() => setEnablePlan(null)}
        open={enablePlan ? true : false}
        buttonArr={CommonButtonArray(
          () => confirmEnableDisable(enablePlan),
          () => setEnablePlan(null),
          "Yes",
          "No"
        )}
      />
      <ModalLayout
        modwidth="34%"
        heading={"Draft Already Exists"}
        content={
          <Typography textAlign="center">
            A draft already exists for this problem. Do you want to continue
            with the existing draft or override it with
            {editDraftSource ? " approved version data?" : " master data?"}
          </Typography>
        }
        onClose={() => {
          setDraftPlanPrompt(null);
          setEditDraftSource(null);
        }}
        open={draftPlanPrompt ? true : false}
        buttonArr={[
          {
            id: "continue-draft",
            label: "Continue Draft",
            clickHandler: () => {
              const planId = CheckNumber(
                draftPlanPrompt?.id || draftPlanPrompt?.planId
              );
              const planVersionId = CheckNumber(draftPlanPrompt?.planVersionId);
              setDraftPlanPrompt(null);
              setEditDraftSource(null);
              navigate(`./${planId}/${planVersionId}/${RouteUrls.plan}`, {
                state: { planVersionId }
              });
            }
          },
          {
            id: "override-draft",
            label: "Override Draft",
            clickHandler: () => {
              const editSource = editDraftSource;
              setDraftPlanPrompt(null);
              setEditDraftSource(null);
              if (editSource) handleEdit(editSource, true);
              else handleCreatePlan(true);
            },
            type: "border"
          }
        ]}
      />
      <Filter2
        filterDataArr={filterData}
        handleFilterClick={(data) => setFilterData(data)}
        changeSearchText={(val) => setSearchText(val)}
        setFilterHeight={(val) => setFilterHeight(val)}
        SampleUIWithFilter={SampleUIWithFilter}
      />
      <TableLayout
        tblBody={TblBodyJSX}
        tblHead={ControlledTable.TblHead(TableCol())}
        loading={loading}
        height={tableHeight}
        pageRecordCount={rows.length}
        totalRecordCount={pageData.total}
        Pagination={
          <ControlledTable.Pagination
            mainPage={pageData.page}
            limit={pageData.limit}
            total={pageData.total}
            handleClick={handleSetData}
            tableStyle={true}
          />
        }
      />
    </Box>
  );
};
export default MyPreApprovedPlans;
